/* prettier-ignore */

import { HttpClient, HttpHeaders } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { tap } from 'rxjs';
import { RestService } from 'src/app/services/rest.service';
import dayjs, { Dayjs } from 'dayjs';
import { TranslationService } from 'src/app/services/translation.service';
import { ToolsService } from 'src/app/services/tools.service';
import { InformerResourcesService } from '../register-user/services/informer-resources.service';
import { AuthService } from 'src/app/auth/services';
import { StatisticsService } from 'src/app/services/statistics.service';
@Injectable({ providedIn: 'root' })

export class RegisterStandaloneService {
    private rest = inject(RestService);
    private http = inject(HttpClient);
    private _translationService = inject(TranslationService);
    private _toolsService = inject(ToolsService);
    private _informerResources = inject(InformerResourcesService);
    private _authService = inject(AuthService);
    private _statisticsService = inject(StatisticsService);

    public tokenRegister: any;
    public idRegister: any;
    public clientId: any;
    public channelId: any;

    /**
     * HRS-3134: registro ya creado para este visitante. Se crea al llegar al último
     * paso y se va actualizando; el token lo firma el back y es lo que le acredita
     * que quien actualiza es quien lo creó (el token del canal es público, va en el
     * QR). No son signals porque nadie los pinta: leerlos desde los `effect` del
     * autoguardado los convertiría en dependencias y cada guardado dispararía otro.
     */
    public registerId: number | null = null;
    private registerToken: string | null = null;

    /**
     * Mientras el borrador está cerrado no se guarda nada. Hace falta porque el
     * envío y el «Volver» dejan atrás un autoguardado programado: al vaciarse el
     * carrito de recursos salta el efecto que lo dispara y, con el registro ya
     * cerrado, creaba un segundo registro del mismo visitante.
     */
    private draftClosed = true;
    
    private dateRange = signal({ from: dayjs().format('YYYY-MM-DD'), to: dayjs().format('YYYY-MM-DD')});
    private startTime = signal<string | null>(null);
    private travelerType = signal(null);
    private categories = signal([]);
    public registerBeforeQuestion = signal(null);
    private registerBeforeAnswer = signal(null);
    public registerAfterQuestion = signal(null);
    private registerAfterAnswer = signal(null);
    public surveyQuestion = signal(null);

    private name = signal<string | null>(null);
    private email = signal<string | null>(null);
    private pdfs = signal([]);
    private extraInfo = signal<string | null>(null);

    public isTravelerTypeError = computed(() => this.travelerType() === null);
    public isInterestTypeError = computed(() => this.categories().length === 0);
    public showRegisterBeforeError = signal(false);
    public showRegisterAfterError = signal(false);

    getRegisterDataStandaloneByToken(token){
        this.tokenRegister = token;
        return this.http.get(`${this.rest.apiUrlBase}/api/get-channelinfo/${token}`);
    }

    getRegisterDataStandaloneById(id){
        this.idRegister = id;
        return this.http.get(`${this.rest.apiUrlBase}/api/get-onlyregisterinfo/${this.rest.subDomain}/${id}`);
    }

    /** Envío final: asocia el visitante a su registro y manda el correo. */
    sendRegisterData(){
        // Lo que quede programado ya no debe escribir: este es el último guardado.
        this.draftClosed = true;

        return this._postRegister(true);
    }

    /**
     * HRS-3134: guarda el registro sin enviar el correo. Se llama al llegar al
     * último paso y cada vez que el informador cambia algo ahí (PDFs, recursos,
     * registro posterior), para que el visitante quede registrado aunque nadie
     * pulse «Enviar»: si se cierra el navegador o se vuelve atrás, el dato está.
     *
     * Si falla no se avisa: el técnico no ha pedido nada y el envío final volverá
     * a intentarlo —sin `register_id` el back inserta, como hasta ahora.
     */
    saveDraft(){
        return this._postRegister(false).pipe(tap((response: any) => {
            if (response?.status !== 'Success')
                return;

            this.registerId = response.register_id;
            this.registerToken = response.register_token;
        }));
    }

    /** El registro del informador empieza a guardarse: se abre su borrador. */
    openDraft(){
        this.draftClosed = false;
    }

    public isDraftOpen(){
        return !this.draftClosed;
    }

    /** Se descarta el borrador en curso, no el registro: ese ya está guardado. */
    resetDraft(){
        this.draftClosed = true;
        this.registerId = null;
        this.registerToken = null;
    }

    private _postRegister(send: boolean){
        const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        const params = this.idRegister || this.tokenRegister;
        const postRegister = this.generateStructure(send);
        return this.http.post(`${this.rest.apiUrlBase}/api/post-register/${params}`, postRegister, {headers})
    }

    /**
     * HRS-2990: hora de inicio del formulario. Se guarda para que el back pueda
     * crear la copia de la ruta predefinida a la hora que el visitante empieza.
     */
    setStartTime(startTime: string | null){
        this.startTime.set(startTime && startTime.trim() !== '' ? startTime : null);
    }

    setCalendarData(dateRange){
        if (!dateRange?.startDate || !dateRange.endDate)
            return;

        this.dateRange.set({
            from: dateRange.startDate,
            to: dateRange.endDate
        })
    }

    setTravelerType(id){
        if (!id)
            return;

        this.travelerType.set(id);
    }

    setCategories(data){
        if (!data || data?.length === 0)
            return;

        this.categories.set(data);
    }

    setRegisterBeforeAnswer(registerAnswer){
        if (!registerAnswer || registerAnswer?.length === 0)
            return;

        this.registerBeforeAnswer.update(prev => registerAnswer);
    }

    resetRegisterBefore(){
        this?.showRegisterBeforeError?.update(prev => false);
        this.registerBeforeAnswer.update(prev => null);
    }

    setRegisterAfterAnswer(registerAnswer){
        if (!registerAnswer || registerAnswer?.length === 0)
            return;

        this.registerAfterAnswer.update(prev => registerAnswer);
    }

    resetRegisterAfter(){
        this?.showRegisterAfterError?.update(prev => false);
        this.registerAfterAnswer.update(prev => null);
    }

    getArrayIndexErrors(registerAnswer){
        return registerAnswer?.map(data => data.required && (!data.answer || data.answer?.length === 0 || data.answer === ''));
    }

    setUserData(user){
        this.name.set(user.name ?? null);
        this.email.set(user.email ?? null);
    }

    setPdfs(pdfs){
        this.pdfs.set(pdfs || []);
    }

    /**
     * HRS-3074: texto libre del informador para el visitante. Viaja como `info`,
     * el mismo nombre que usa el envío de ruta por correo, y el back lo guarda en
     * la misma columna.
     */
    setExtraInfo(extraInfo: string | null){
        const texto = (extraInfo ?? '').trim();
        this.extraInfo.set(texto === '' ? null : texto);
    }

    generateStructure(send: boolean = true){
        return {
            // HRS-3134: `send` separa guardar de enviar el correo, y el par
            // id + token hace que el back actualice el registro ya creado para
            // este visitante en vez de crear otro.
            send,
            register_id: this.registerId,
            register_token: this.registerToken,
            register: {
                calendar: this.dateRange(),
                // Hora a la que arranca el viaje: el back la usa al instanciar la
                // ruta predefinida que se envía en el correo.
                startTime: this.startTime(),
                travelerType: this.travelerType(),
                selectedCategories: this.categories(),
                clientID: this.clientId,
                // HRS-2990: la app del registro. El back la necesita para las rutas
                // predefinidas, cuyo listado el asistente sirve por cliente y app.
                appID: this.rest.device()?.appID ?? null,
                lang: this._translationService.selectedLanguage(),
                registerBefore: this.registerBeforeAnswer(),
                registerAfter: this.registerAfterAnswer(),
                pdfs: this.pdfs(),
                // HRS-3074: «Información adicional» del formulario final.
                info: this.extraInfo(),
                domain: window.location.origin,
                userData: {
                    email: this.email(),
                    name: this.name()
                },
                // HRS-2990: modo con el que se registró y recursos que el informador
                // ha añadido para el correo. Solo van id y tipo (y el token en las
                // rutas predefinidas, que es lo que las identifica): el nombre, la
                // imagen y el enlace los resuelve el back en el idioma del envío.
                mode: this._toolsService.informerMode(),
                // Solo el registro completo manda recursos: el carrito sobrevive dos
                // horas en el localStorage, así que un simple abierto a continuación
                // le habría enviado al visitante los del anterior. El carrito no se
                // borra, para que el completo lo recupere si el técnico vuelve a él.
                resources: this._toolsService.informerMode() === 'complete'
                  ? this._informerResources.payload()
                  : [],
                // Atribución del registro, como ya hacía el planner: si hay sesión
                // en la webapp, el registro queda ligado a ese usuario.
                users_webapp_id: this._authService.user()?.id ?? null,
                ...this._statisticsService.cmsMetricsIdentity(),
            }
        }
    }

    isErrorFromRegisterBefore(){
        if (!this.registerBeforeQuestion()?.[this._translationService.selectedLanguage()]?.some(answer => answer.required))
            return false;

        if (this.registerBeforeAnswer())
            return this.registerBeforeAnswer()?.filter(answer => answer.required)?.some(data => !data.answer || data.answer?.length === 0 || data.answer === '');

        return true;
    }

    isErrorFromRegisterAfter(){
        if (!this.registerAfterQuestion()?.[this._translationService.selectedLanguage()]?.some(answer => answer.required))
            return false;

        if (this.registerAfterAnswer())
            return this.registerAfterAnswer()?.filter(answer => answer.required)?.some(data => !data.answer || data.answer?.length === 0 || data.answer === '');

        return true;
    }
}